import { useApolloClient } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import {
  useDeleteCertificationProgressMutation,
  ListCertificationProgressDocument,
} from '@/@generated/codegen/API';
import { CERTIFICATION_PROGRESS_QUERY_KEYS } from '@/hooks/queries/useCertificationProgress';

/**
 * Hook for resetting certification progress
 * Completely isolated from the rest of the application
 * Designed for future admin-only access
 */
export const useResetCertification = () => {
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [deleteMutation] = useDeleteCertificationProgressMutation();

  const resetCertification = useCallback(
    async (userId: string, caseId: string, masterclassId: string): Promise<boolean> => {
      setIsResetting(true);
      setError(null);

      try {
        // First, get all certification progress entries for this user and case
        const listResult = await apolloClient.query({
          query: ListCertificationProgressDocument,
          variables: {
            where: {
              userId: { equals: userId },
              caseId: { equals: caseId },
              ...(masterclassId && { masterclassId: { equals: masterclassId } }),
            },
          },
        });

        // Delete each certification progress entry
        const edges = listResult.data?.listCertificationProgress?.edges || [];
        for (const edge of edges) {
          const result = await deleteMutation({
            variables: {
              where: { id: edge.node.id },
            },
          });

          if (result.errors) {
            console.error('[ResetCertification] Error deleting record:', result.errors);
            setError(result.errors[0].message);
            return false;
          }
        }

        console.log(
          '[ResetCertification] Successfully reset certification for user:',
          userId,
          'case:',
          caseId
        );

        // Invalidate all related queries
        if (masterclassId) {
          void queryClient.invalidateQueries({
            queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(
              userId,
              caseId,
              masterclassId
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.bestPassed(userId, caseId, masterclassId),
          });
          void queryClient.invalidateQueries({
            queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(caseId, masterclassId),
          });
        }
        void apolloClient.refetchQueries({
          include: ['listCertificationProgress'],
        });

        // Dispatch event to notify other components
        window.dispatchEvent(
          new CustomEvent('certification-updated', {
            detail: { userId, caseId, masterclassId, action: 'reset' },
          })
        );

        return true;
      } catch (err) {
        console.error('[ResetCertification] Unexpected error:', err);
        setError('An unexpected error occurred');
        return false;
      } finally {
        setIsResetting(false);
      }
    },
    [queryClient, apolloClient, deleteMutation]
  );

  return {
    resetCertification,
    isResetting,
    error,
  };
};
